/**
 * pageStyles.js
 *
 * Constantes de estilo compartilhadas para manter consistência entre páginas.
 * Importe esses objetos como `sx` prop nos componentes MUI.
 *
 * Uso:
 *   import { pageHeader, filterPaper, filterHeader } from '../styles/pageStyles';
 *   <Box sx={pageHeader}>...</Box>
 */

/** Linha de cabeçalho da página (título + botão de ação). */
export const pageHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: { xs: 'flex-start', sm: 'center' },
  flexDirection: { xs: 'column', sm: 'row' },
  mb: 2,
  flexWrap: 'wrap',
  gap: 1,
};

/** Paper que envolve o bloco de filtros. */
export const filterPaper = {
  p: 2,
  mb: 2,
};

/** Linha de cabeçalho interno do bloco de filtros (ícone + label + botão limpar). */
export const filterHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 1.5,
  flexWrap: 'wrap',
};

/** Linha de filtros em linha única com wrap automático no mobile. */
export const filterRow = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

/** Props de tipografia para o rótulo "Filtros:". */
export const filterLabelProps = {
  variant: 'subtitle2',
  color: 'text.secondary',
  fontWeight: 'medium',
};

/** Grid de filtros com espaçamento padrão. */
export const filterGrid = {
  mt: 0,
};

/** Card de listagem padrão. */
export const listCard = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

/** Caixa de descrição abaixo do título de página. */
export const pageSubtitle = {
  variant: 'body2',
  color: 'text.secondary',
  sx: { mt: 0.25 },
};

/** TableContainer padronizado com Paper outlined. */
export const tableContainer = {
  borderRadius: 2,
};

/** Estilo padronizado para headers de tabela. */
export const tableHeadCell = {
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'text.secondary',
  py: 1,
};

/** Estilo padronizado para células de corpo em tabelas. */
export const tableBodyCell = {
  py: 0.75,
  px: 1.5,
};

/** Header de seção dentro de cards (ícone + texto). */
export const cardSectionHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  mb: 1,
};

/** Estilo de texto para headers de seção dentro de cards. */
export const cardSectionHeaderText = {
  variant: 'subtitle2',
  fontWeight: 600,
  fontSize: '0.8125rem',
  letterSpacing: '0.01em',
  color: 'text.primary',
};

/** Empty state padrão dentro de cards do dashboard. */
export const cardEmptyState = {
  textAlign: 'center',
  py: 2,
};

/** Card padrão de listagem com borda accent à esquerda. */
export const standardCard = {
  elevation: 0,
  border: '1px solid',
  borderColor: 'divider',
  borderLeft: '3px solid',
  borderLeftColor: 'primary.main',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

/** Card de dashboard sem accent border. */
export const dashboardCard = {
  elevation: 0,
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

/** Background padronizado para linha de header de tabela. */
export const tableHeadRow = {
  bgcolor: 'grey.50',
};

/** Props padronizadas para o título principal da página. */
export const pageTitleProps = {
  variant: 'h4',
  fontWeight: 700,
  color: 'primary.dark', // G8 — azul mais escuro da designer (#1565c0)
};

/** Container central para páginas de conteúdo denso (form/config). Listagens NÃO usam. */
export const pageContainer = {
  maxWidth: 1440,
  mx: 'auto',
  width: '100%',
};

/** Largura de leitura para um bloco de formulário linear (evita campo esticado em ultrawide). */
export const formColumn = {
  maxWidth: 720,
  width: '100%',
};
